import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Link,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiAlertCircle, FiAlertOctagon } from 'react-icons/fi';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import LinkPaymentsModal from './LinkPayments/LinkPaymentsModal';

function getFullNameFromStudent(student) {
  const u = student?.user || {};
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
}

const MONTH_LABELS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const currentMonth1Based = new Date().getMonth() + 1;

function getInitials(student) {
  const u = student?.user || {};
  const first = (u.first_name || '').trim().charAt(0) || '';
  const last = (u.last_name || '').trim().charAt(0) || '';
  return (first + last).toUpperCase() || '?';
}

function getFullName(student) {
  const u = student?.user || {};
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
}

/**
 * Tabla de seguimiento mensual de pagos (programas anualizados / mensualidad).
 * Comportamiento idéntico al reporte previo — extraída para poder bifurcar
 * la vista según program_type.is_semester_based sin romper regresión.
 */
export default function MonthlyTable({ data = [], onReload }) {
  const [linkModalRow, setLinkModalRow] = useState(null);
  const [localOrphanDelta, setLocalOrphanDelta] = useState({});
  useEffect(() => { setLocalOrphanDelta({}); }, [data]);

  const openLinkModal = useCallback((row) => setLinkModalRow(row), []);
  const closeLinkModal = useCallback(() => setLinkModalRow(null), []);
  const handleLinkSaved = useCallback((savedCount) => {
    if (!linkModalRow?.enrollmentId || !savedCount) return;
    setLocalOrphanDelta((prev) => ({
      ...prev,
      [linkModalRow.enrollmentId]: (prev[linkModalRow.enrollmentId] || 0) + savedCount,
    }));
    if (onReload) setTimeout(() => onReload(), 900);
  }, [linkModalRow, onReload]);

  return (
    <>
    <TableContainer overflowX="auto" maxW="100%">
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th
              position="sticky"
              left={0}
              zIndex={2}
              bg="gray.50"
              minW="220px"
              borderRightWidth="1px"
              py={3}
              px={4}
            >
              ESTUDIANTE
            </Th>
            {MONTH_LABELS.map((label) => (
              <Th key={label} minW="52px" textAlign="center" bg="gray.50" py={3}>
                {label}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row) => (
            <Tr key={row.enrollmentId ?? `${row.student?.id}-${row.programId}`}>
              <Td
                position="sticky"
                left={0}
                zIndex={1}
                bg="white"
                borderRightWidth="1px"
                borderColor="gray.100"
              >
                <Flex align="center" gap={3}>
                  <Flex
                    w={10}
                    h={10}
                    borderRadius="full"
                    bg="#E8E4FC"
                    color="#574feb"
                    borderWidth="1px"
                    borderColor="#D4CCF0"
                    align="center"
                    justify="center"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {getInitials(row.student)}
                  </Flex>
                  <Box>
                    <Flex align="center" gap={2} flexWrap="wrap">
                      <Link
                        href={`/plataforma/administrador/ver-estudiante/${row.student?.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontWeight="medium"
                        color="blue.600"
                        _hover={{ underline: true }}
                      >
                        {getFullName(row.student)}
                      </Link>
                      {(row.needsMonthReview || row.needsSemesterAssignment) && row.enrollmentId != null && row.enrollmentType && (
                        <Tooltip
                          label={
                            row.needsSemesterAssignment
                              ? 'Meses sin semestre asignado — clic para vincular'
                              : 'Pagos sin meses asignados — clic para vincular'
                          }
                          placement="top"
                          hasArrow
                        >
                          <IconButton
                            aria-label={row.needsSemesterAssignment ? 'Vincular semestre' : 'Vincular meses'}
                            icon={<Box as={FiAlertOctagon} boxSize={4} />}
                            size="xs"
                            variant="ghost"
                            color="gray.500"
                            _hover={{ color: 'orange.500' }}
                            onClick={() => openLinkModal(row)}
                          />
                        </Tooltip>
                      )}
                    </Flex>
                    <Text fontSize="xs" color="gray.500">
                      ID: {row.student?.id ?? '—'}
                    </Text>
                  </Box>
                </Flex>
              </Td>
              {(row.months || []).map((m) => (
                <Td key={m.month} textAlign="center" minW="52px">
                  {m.status === 'PAID' && (
                    <Flex justify="center">
                      <Box
                        bg="green.100"
                        borderRadius="lg"
                        p={2}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        title="Pagado"
                      >
                        <Box as={IoIosCheckmarkCircleOutline} color="green.600" boxSize={6} />
                      </Box>
                    </Flex>
                  )}
                  {m.status === 'LATE' && (
                    <Flex justify="center">
                      <Box
                        bg="red.100"
                        borderRadius="lg"
                        p={2}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        title="Vencido"
                      >
                        <Box as={FiAlertTriangle} color="red.600" boxSize={6} />
                      </Box>
                    </Flex>
                  )}
                  {m.status === 'PENDING' && m.month === currentMonth1Based && (
                    <Flex justify="center">
                      <Box
                        bg="orange.100"
                        borderRadius="lg"
                        p={2}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        title="Próximo a vencer"
                      >
                        <Box as={FiAlertCircle} color="orange.600" boxSize={6} />
                      </Box>
                    </Flex>
                  )}
                  {m.status === 'PENDING' && m.month !== currentMonth1Based && (
                    <Flex justify="center">
                      <Box w={2} h={2} borderRadius="full" bg="gray.300" title="Por vencer" />
                    </Flex>
                  )}
                  {m.status === 'N/A' && <Box />}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>

    <LinkPaymentsModal
      isOpen={!!linkModalRow}
      onClose={closeLinkModal}
      studentId={linkModalRow?.student?.id}
      programId={linkModalRow?.programId}
      enrollmentType={linkModalRow?.enrollmentType}
      enrollmentId={linkModalRow?.enrollmentId}
      studentName={linkModalRow ? getFullNameFromStudent(linkModalRow.student) : ''}
      programName={linkModalRow?.programName}
      semesterName={linkModalRow?.semesterName}
      onSaved={handleLinkSaved}
    />
    </>
  );
}
